import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { lessonService } from '@/services';
import Navigation from '@/components/layout/Navigation';
import LessonSelection from '@/components/learn/LessonSelection';
import LevelIntro from '@/components/learn/LevelIntro';
import LevelComplete from '@/components/learn/LevelComplete';
import LessonComplete from '@/components/learn/LessonComplete';
import LevelExercise from '@/components/learn/LevelExercise';
import LearningProgress from '@/components/learn/LearningProgress';
import { useLevelState } from '@/hooks/useLevelState';
import AchievementChecker from '@/components/achievement/AchievementChecker';
import { PerfectLevelProvider, usePerfectLevel } from '@/contexts/PerfectLevelContext';

const LearnContent = () => {
  const { user } = useAuth();
  const [triggerAchievementCheck, setTriggerAchievementCheck] = useState(false);
  
  const {
    selectedLesson,
    currentExerciseIndex,
    showAnswer,
    userInput,
    isInputCorrect,
    exercises,
    showLevelIntro,
    showLevelComplete,
    showLessonComplete,
    wordsToReview,
    totalOriginalExercises,
    currentExercise,
    progress,
    currentLevel,
    totalXpGained,
    levels,
    totalExercises,
    isPerfectLevel,
    isPerfectLesson,
    lessonBonus,
    isLastExercise,
    displayedExerciseNumber,
    previouslyCompletedLevels,
    currentLevelIndex,
    levelCompletionCounts,
    isPracticeMode,
    
    setUserInput,
    setWordsToReview,
    handleSelectLesson,
    handleNextExercise,
    handlePrevExercise,
    handleCheckAnswer,
    handleBackToLessons,
    moveToNextLevel,
    skipToNextLevel,
    isLevelPreviouslyCompleted,
    setShowLevelIntro,
    completeLevel,
    startLevel,
    handleImperfectLesson,
    
    onLogActivity
  } = useLevelState({ userId: user?.id });
  
  const { data: lessons = [], isLoading } = useQuery({
    queryKey: ['lessons', user?.id],
    queryFn: () => user ? lessonService.getUserLessons(user.id) : Promise.resolve([]),
    enabled: !!user,
  });
  
  const activeLessons = lessons.filter(lesson => lesson.active);
  
  const isCurrentExerciseRecycled = React.useMemo(() => {
    if (!currentExercise) return false;
    
    const isInReviewList = wordsToReview.includes(currentExercise.id);
    const occurrences = exercises.filter(ex => ex.id === currentExercise.id).length;
    const appearsMultipleTimes = occurrences > 1;
    const currentIndex = exercises.findIndex(ex => ex.id === currentExercise.id);
    const isBeyondOriginalExercises = currentIndex >= totalOriginalExercises && totalOriginalExercises > 0;
    
    return isInReviewList || appearsMultipleTimes || isBeyondOriginalExercises;
  }, [currentExercise, exercises, wordsToReview, totalOriginalExercises]);
  
  const getAdjustedXpReward = (levelIndex: number) => {
    const originalXP = levels[levelIndex]?.xpReward || 0;
    const completionCount = levelCompletionCounts[levelIndex] || 0;
    return Math.max(1, Math.ceil(originalXP / (completionCount + 1)));
  };
  
  const handleBackToLessonsWithAchievementCheck = () => {
    if (user?.id) {
      console.debug('🏆 [Learn] Triggering achievement check after lesson is fully closed');
      setTriggerAchievementCheck(true);
      
      setTimeout(() => {
        setTriggerAchievementCheck(false);
      }, 500);
    }
    
    handleBackToLessons();
  };

  useEffect(() => {
    if (user?.id) {
      const pendingAchievementCheck = localStorage.getItem('pendingAchievementCheck');
      if (pendingAchievementCheck === 'true') {
        console.debug('🏆 [Learn] Triggering achievement check on app reload');
        setTriggerAchievementCheck(true);
        
        setTimeout(() => {
          localStorage.removeItem('pendingAchievementCheck');
          setTriggerAchievementCheck(false);
        }, 1000);
      }
      
      // Check for daily quest in session storage
      const storedLesson = sessionStorage.getItem('selectedLesson');
      if (storedLesson && !selectedLesson) {
        try {
          const parsedLesson = JSON.parse(storedLesson);
          console.log('🎯 [Learn] Found daily quest in sessionStorage:', parsedLesson.name);
          handleSelectLesson(parsedLesson);
          sessionStorage.removeItem('selectedLesson'); // Clear to prevent reloading
        } catch (error) {
          console.error('Error parsing stored lesson:', error);
        }
      }
    }
  }, [user?.id, handleSelectLesson, selectedLesson]);
  
  useEffect(() => {
    if (showLessonComplete && user?.id) {
      localStorage.setItem('pendingAchievementCheck', 'true');
      console.debug('🏆 [Learn] Set pending achievement check flag for reload handling');
    }
  }, [showLessonComplete, user?.id]);

  const finalTotalXp = (window as any).__TOTAL_XP_GAINED__ || totalXpGained + (isPerfectLesson ? lessonBonus : 0);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse">Loading lessons...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container max-w-md mx-auto px-4 py-6">
        <header className="mb-6">
          <div className="flex justify-between items-center mb-4">
            {(selectedLesson || showLevelIntro || showLevelComplete || showLessonComplete) ? (
              <Button variant="ghost" size="icon" onClick={handleBackToLessonsWithAchievementCheck}>
                <ArrowLeft className="h-6 w-6" />
              </Button>
            ) : (
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-6 w-6" />
                </Button>
              </Link>
            )}
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <span className="font-medium">
                {selectedLesson ? selectedLesson.name : 'Choose a Lesson'}
              </span>
            </div>
            {selectedLesson && !showLevelIntro && !showLevelComplete && !showLessonComplete && (
              <div className="flex items-center gap-1 bg-muted/50 px-3 py-1 rounded-full">
                <Sparkles className="h-4 w-4 text-yellow-400" />
                <span className="font-medium">Level {currentLevelIndex + 1}</span>
              </div>
            )}
          </div>
          
          {selectedLesson && exercises.length > 0 && !showLevelIntro && !showLevelComplete && !showLessonComplete && (
            <LearningProgress 
              currentExerciseIndex={displayedExerciseNumber} 
              totalExercises={totalExercises}
              progress={progress}
              isRecycledWord={isCurrentExerciseRecycled}
            />
          )}
        </header>
        
        {showLevelIntro && (
          <LevelIntro 
            level={currentLevel}
            levelIndex={currentLevelIndex}
            onStartLevel={() => setShowLevelIntro(false)}
            onSkipLevel={skipToNextLevel}
            isPreviouslyCompleted={isLevelPreviouslyCompleted(currentLevelIndex)}
            adjustedXpReward={getAdjustedXpReward(currentLevelIndex)}
          />
        )}
        
        {showLevelComplete && (
          <>
            {console.debug(`[Learn] Rendering LevelComplete with isPerfectLevel=${isPerfectLevel}`)}
            <LevelComplete 
              level={currentLevel} 
              currentLevelIndex={currentLevelIndex} 
              totalLevels={levels.length} 
              onNextLevel={moveToNextLevel} 
              isPerfectLevel={isPerfectLevel}
              adjustedXpReward={getAdjustedXpReward(currentLevelIndex)}
              isPracticeMode={isPracticeMode}
            />
          </>
        )}
        
        {showLessonComplete && (
          <>
            {console.debug(`[Learn] Rendering LessonComplete with isPerfectLesson=${isPerfectLesson}, lessonBonus=${lessonBonus}`)}
            <LessonComplete 
              totalXpGained={finalTotalXp}
              levels={levels} 
              lessonName={selectedLesson?.name || ''} 
              onBackToLessons={handleBackToLessonsWithAchievementCheck}
              isPerfectLesson={isPerfectLesson}
              lessonBonus={lessonBonus}
              levelCompletionCounts={levelCompletionCounts}
              triggerAchievementCheck={setTriggerAchievementCheck}
              isDailyQuest={selectedLesson?.isDailyQuest}
              dailyQuestBonus={selectedLesson?.dailyQuestBonus || 25}
            />
          </>
        )}
        
        {!selectedLesson && !showLevelIntro && !showLevelComplete && !showLessonComplete && (
          <LessonSelection 
            activeLessons={activeLessons} 
            onSelectLesson={handleSelectLesson} 
          />
        )}
        
        {selectedLesson && currentExercise && !showLevelIntro && !showLevelComplete && !showLessonComplete && (
          <LevelExercise 
            currentExercise={currentExercise}
            showAnswer={showAnswer}
            userInput={userInput}
            isInputCorrect={isInputCorrect}
            onUserInputChange={setUserInput}
            onCheck={handleCheckAnswer}
            onNext={handleNextExercise}
            onPrev={handlePrevExercise}
            isLastExercise={isLastExercise}
            exerciseNumber={displayedExerciseNumber}
            totalExercises={totalExercises}
            onClose={handleBackToLessonsWithAchievementCheck}
            exercises={exercises}
            totalOriginalExercises={totalOriginalExercises}
            wordsToReview={wordsToReview}
            onComplete={completeLevel}
            onLogActivity={onLogActivity}
            isPerfectLevel={isPerfectLevel}
            onImperfectLevel={handleImperfectLesson}
            onAddWordToReview={(wordId) => {
              console.log(`⚠️ [Learn] Adding word ${wordId} to words to review for recycling`);
              setWordsToReview(prev => [...prev.filter(id => id !== wordId), wordId]);
            }}
          />
        )}
      </div>
      
      <AchievementChecker triggerCheck={triggerAchievementCheck}>
        <div style={{ display: 'none' }}></div>
      </AchievementChecker>
      <Navigation />
    </div>
  );
};

const Learn = () => {
  return (
    <PerfectLevelProvider>
      <LearnContent />
    </PerfectLevelProvider>
  );
};

export default Learn;
